import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ListChecks, Sparkles, Gift, Clock } from "lucide-react";

interface JobDetailContentProps {
  job: any;
}

/**
 * Renders all optional content sections for a job detail page.
 * Sections with empty data are automatically hidden.
 */
export function JobDetailContent({ job }: JobDetailContentProps) {
  const responsibilities = (job.responsibilities as string[]) || [];
  const requirementsMust = (job.requirements_must as string[]) || [];
  const requirementsNice = (job.requirements_nice as string[]) || [];
  const benefits = (job.benefits as string[]) || [];
  const processSteps = (job.process_steps as string[]) || [];
  const skillsTags: string[] = Array.isArray((job as any).skills_tags) ? (job as any).skills_tags : [];

  return (
    <>
      {/* Skills tags */}
      {skillsTags.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Skills desta vaga</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {skillsTags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">{tag}</span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Description */}
      {job.description && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Sobre a vaga</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="text-muted-foreground prose prose-sm max-w-none dark:prose-invert
                [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              dangerouslySetInnerHTML={
                containsHtml(job.description)
                  ? { __html: sanitizeBasicHtml(job.description) }
                  : undefined
              }
              children={containsHtml(job.description) ? undefined : job.description}
            />
          </CardContent>
        </Card>
      )}

      {/* Responsibilities */}
      {responsibilities.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-primary" />O que você vai fazer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {responsibilities.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Requirements */}
      {(requirementsMust.length > 0 || requirementsNice.length > 0) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-accent" />Requisitos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {requirementsMust.length > 0 && (
              <div>
                <p className="font-medium text-foreground mb-2 text-sm">Obrigatórios:</p>
                <ul className="space-y-1">
                  {requirementsMust.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-primary mt-0.5">•</span>
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {requirementsNice.length > 0 && (
              <div>
                <p className="font-medium text-foreground mb-2 text-sm">Diferenciais:</p>
                <ul className="space-y-1">
                  {requirementsNice.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-accent mt-0.5">•</span>
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Benefits */}
      {benefits.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Gift className="h-4 w-4 text-green-600 dark:text-green-400" />Benefícios
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {benefits.map((item, i) => (
                <Badge key={i} variant="secondary">{item}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Process Steps */}
      {processSteps.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />Etapas do processo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3">
              {processSteps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-muted-foreground text-sm">{step}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}
    </>
  );
}

/** Simple check for HTML tags in description text */
function containsHtml(text: string): boolean {
  return /<[a-z][\s\S]*>/i.test(text);
}

/** Strip dangerous tags but keep basic formatting */
function sanitizeBasicHtml(html: string): string {
  const allowed = ["p", "br", "b", "strong", "i", "em", "ul", "ol", "li", "a", "span", "div", "h1", "h2", "h3", "h4"];
  return html.replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, (match, tag) => {
    if (allowed.includes(tag.toLowerCase())) return match;
    return "";
  }).replace(/<script[\s\S]*?<\/script>/gi, "");
}
